import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Cloud, Zap } from "lucide-react";

const navLinks = [
  { label: "VPS", href: "/vps-hosting" },
  { label: "Cloud", href: "/cloud-hosting" },
  { label: "Shared", href: "/shared-hosting" },
  { label: "WordPress", href: "/wordpress-hosting" },
  {
    label: "Solutions",
    children: [
      { label: "BaaS & DRaaS", href: "/baas-draas" },
      { label: "For E-Commerce", href: "/solutions" },
      { label: "For Agencies", href: "/solutions" },
      { label: "For Developers", href: "/solutions" },
    ],
  },
  {
    label: "Enterprise",
    children: [
      { label: "Trust & Security", href: "/security" },
      { label: "Compare Providers", href: "/compare" },
    ],
  },
  {
    label: "More",
    children: [
      { label: "Domains", href: "/domains" },
      { label: "SSL Certificates", href: "/ssl" },
      { label: "Email Hosting", href: "/email-hosting" },
      { label: "Blog", href: "/blog" },
      { label: "Refer & Earn", href: "/refer" },
      { label: "Contact Us", href: "/contact" },
    ],
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className="fixed md:top-12 top-4 left-1/2 z-50 transition-all duration-300 w-[90%] md:w-[95%] lg:w-[1200px]"
      style={{
        transform: "translateX(-50%)",
        width: "90%",
        maxWidth: "1200px",
        background: scrolled
          ? "rgba(3, 3, 5, 0.95)"
          : "rgba(10, 10, 18, 0.8)",
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        border: "1px solid rgba(0, 243, 255, 0.15)",
        borderRadius: "100px",
        padding: "10px 24px",
        boxShadow: scrolled ? "0 0 30px rgba(0, 243, 255, 0.1)" : "none",
      }}
    >
      <div className="flex items-center w-full">
        {/* Left Side: Logo + Nav Links clustered together */}
        <div className="flex items-center gap-8">
          {/* Logo */}
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 no-underline">
            <div
              className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{
                background: "linear-gradient(135deg, #00F3FF, #BC13FE)",
                boxShadow: "0 0 15px rgba(0, 243, 255, 0.4)",
              }}
            >
              <Cloud size={24} color="#030305" strokeWidth={2.5} />
            </div>
            <span
              className="font-orbitron font-700 text-xl tracking-wider"
              style={{
                background: "linear-gradient(to right, #00F3FF, #BC13FE)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              INDIA<span style={{ color: "#BC13FE" }}>CLOUD</span>.AI
            </span>
          </Link>

          {/* Desktop Links (start immediately after logo, proper balance) */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label} className="relative">
                <button
                    data-testid={`nav-${link.label.toLowerCase()}`}
                    onClick={() => setOpenDropdown(openDropdown === link.label ? null : link.label)}
                    className="flex items-center gap-1 px-4 py-2 rounded-full text-[17px] font-rajdhani font-600 text-white hover:text-[#00F3FF] transition-all duration-200"
                    style={{ letterSpacing: "0.03em" }}
                  >
                    {link.label}
                    <ChevronDown size={14} className={`transition-transform ${openDropdown === link.label ? "rotate-180" : ""}`} />
                  </button>
                  {openDropdown === link.label && (
                    <div
                      className="absolute top-full mt-2 left-0 z-50 rounded-xl overflow-hidden"
                      style={{
                        background: "rgba(10, 10, 18, 0.97)",
                        border: "1px solid rgba(0, 243, 255, 0.2)",
                        backdropFilter: "blur(20px)",
                        minWidth: "180px",
                        boxShadow: "0 10px 40px rgba(0, 0, 0, 0.5), 0 0 20px rgba(0, 243, 255, 0.1)",
                      }}
                    >
                      {link.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          onClick={() => setOpenDropdown(null)}
                          className="block px-5 py-3 text-sm font-outfit text-white hover:text-[#00F3FF] hover:bg-white/5 transition-all no-underline"
                        >
                          {child.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                  className="px-4 py-2 rounded-full text-[17px] font-rajdhani font-600 no-underline transition-all duration-200 text-white hover:text-[#00F3FF]"
                  style={{
                    background:
                      location.pathname === link.href
                        ? "rgba(0, 243, 255, 0.08)"
                        : "transparent",
                    letterSpacing: "0.03em",
                  }}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>
        </div>

        {/* Mobile toggle (pushed to right) */}
        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setMobileOpen(!mobileOpen)}
          className="lg:hidden ml-auto text-gray-300 hover:text-white transition-colors"
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden mt-4 pb-4 border-t border-white/5 pt-4">
          <div className="flex flex-col gap-2">
            {navLinks.map((link) =>
              link.children ? (
                <div key={link.label}>
                  {link.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      onClick={() => setMobileOpen(false)}
                      className="block px-3 py-2 text-sm text-gray-300 hover:text-white no-underline"
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="block px-3 py-2 text-sm text-gray-300 hover:text-white no-underline"
                >
                  {link.label}
                </Link>
              )
            )}
            <Link to="/plan-recommender" onClick={() => setMobileOpen(false)} className="mt-2 no-underline">
              <button className="btn-neon w-full text-sm">AI Pick My Plan</button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}
